import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  FrameworkCompanyVendorRow,
  FrameworkDetail,
  FrameworkDeviceVendorRow,
  HealthBand,
} from '@mvcp/contracts';
import { getFrameworkDetail } from '../api';
// One owner for the band vocabulary, pill and score number (Story 8.1). `BAND_ORDER`
// below stays local: it is sort weight, not vocabulary.
import { BandPill, CoverageNote, ScoreValue } from './bands';
import { ProgressBar } from './ProgressBar';
import { SegmentBar } from './SegmentBar';

/**
 * Worst-first ordering weight (Story 4.3 Design Note): `bad < warn < neutral < ok`.
 * Deliberately NOT `HEALTH_BANDS` (whose order is not severity) — a small local map.
 */
const BAND_ORDER: Record<HealthBand, number> = { bad: 0, warn: 1, neutral: 2, ok: 3 };

/** The four count-badged filters, over the FULL row set. */
type FilterKey = 'certified' | 'expiring' | 'atRisk' | 'pending';
const FILTER_LABEL: Record<FilterKey, string> = {
  certified: 'Fully Certified',
  expiring: 'Has Expiring',
  atRisk: 'Has At-Risk',
  pending: 'Has Pending',
};
const FILTER_ORDER: FilterKey[] = ['certified', 'expiring', 'atRisk', 'pending'];

function deviceFlags(r: FrameworkDeviceVendorRow): Record<FilterKey, boolean> {
  return { certified: r.band === 'ok', expiring: r.expiring > 0, atRisk: r.band === 'bad', pending: r.pending > 0 };
}
function companyFlags(r: FrameworkCompanyVendorRow): Record<FilterKey, boolean> {
  return {
    certified: r.band === 'ok',
    expiring: r.statusLabel === 'Expiring',
    atRisk: r.band === 'bad',
    pending: r.statusLabel === 'Pending',
  };
}

/** Sort key: parse an expiry to millis; a missing/unparseable date sorts last. */
function expiryMillis(iso: string | null): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/** Short calendar date (`YYYY-MM-DD`) for a mono expiry cell; em-dash when absent. */
function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  return Number.isNaN(t) ? iso : new Date(t).toISOString().slice(0, 10);
}

type DetailState =
  | { kind: 'loading' }
  | { kind: 'ready'; detail: FrameworkDetail }
  | { kind: 'error'; message: string };

/**
 * Compliance Manager cockpit → per-Framework detail (Story 4.3, FR-16/FR-17). The
 * scope-aware per-Framework surface: device-scoped Frameworks (E27, Class) show
 * per-Vendor device counts + a proportional segment bar; company-scoped ones (ISO,
 * NIS2, GDPR) show per-Vendor status/expiration/approving-authority. Count-badged
 * filters + worst-status-first default sort. Every value is server-computed by
 * consuming `@mvcp/domain`; this view only presents it.
 */
export function FrameworkDetailView({
  frameworkId,
  onBack,
  onCreateReport,
}: {
  frameworkId: string;
  onBack: () => void;
  /** Open the shared report scope-picker pre-selected to this framework (Story 6.1). */
  onCreateReport?: (frameworkId: string) => void;
}): React.JSX.Element {
  const [state, setState] = useState<DetailState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const detail = await getFrameworkDetail(frameworkId);
      setState({ kind: 'ready', detail });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, [frameworkId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <button
        type="button"
        className="icon-button"
        onClick={onBack}
        style={{ alignSelf: 'flex-start' }}
      >
        ← Back
      </button>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading framework detail">
          <p className="muted" style={{ margin: 0 }}>Loading…</p>
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load the framework</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && <ReadyDetail detail={state.detail} onCreateReport={onCreateReport} />}
    </>
  );
}

function ReadyDetail({
  detail,
  onCreateReport,
}: {
  detail: FrameworkDetail;
  onCreateReport?: (frameworkId: string) => void;
}): React.JSX.Element {
  const [filter, setFilter] = useState<FilterKey | null>(null);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15 }} data-numeric>
          {detail.code}
        </span>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          {detail.name}
        </h1>
        <BandPill band={detail.score.band} />
        {onCreateReport && (
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => onCreateReport(detail.frameworkId)}
            style={{ marginLeft: 'auto' }}
          >
            Create report
          </button>
        )}
      </div>

      <section className="card stack" aria-labelledby="fw-score-heading">
        <h2 id="fw-score-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{detail.title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <ScoreValue score={detail.score} name="Framework score" />
          <span className="muted" style={{ fontSize: 13 }} data-numeric>
            {detail.score.compliant} of {detail.score.applicable} compliant
          </span>
          {/* AD-22: `applicable` is the IN-SCOPE subject count, so "1 of 10 compliant"
              alone cannot say whether the other nine were assessed at all. Not
              announced — `ScoreValue`'s name beside it is the single spoken source. */}
          <CoverageNote score={detail.score} />
        </div>
        {/* Names THIS score: the bar's default is the fleet's "Overall score", which
            is a different number and was being announced on a framework page. */}
        <ProgressBar
          percentage={detail.score.percentage}
          band={detail.score.band}
          label="Framework score"
        />
      </section>

      {detail.scope === 'device' ? (
        <DeviceBody rows={detail.rows} filter={filter} setFilter={setFilter} />
      ) : (
        <CompanyBody rows={detail.rows} filter={filter} setFilter={setFilter} />
      )}
    </>
  );
}

/** Count-badged filter chips over the full row set + a one-action Clear. */
function FilterChips({
  counts,
  filter,
  onSelect,
}: {
  counts: Record<FilterKey, number>;
  filter: FilterKey | null;
  onSelect: (f: FilterKey | null) => void;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <div role="group" aria-label="Filter rows" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {FILTER_ORDER.map((key) => {
          const isActive = filter === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(isActive ? null : key)}
              aria-pressed={isActive}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 12px',
                borderRadius: 'var(--rounded-full)',
                border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
                background: isActive ? 'var(--accent-soft)' : 'transparent',
                color: isActive ? 'var(--accent-on-soft)' : 'var(--text-2)',
                fontFamily: 'var(--font-sans)',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {FILTER_LABEL[key]}
              <span data-numeric style={{ opacity: 0.8 }}>
                {counts[key]}
              </span>
            </button>
          );
        })}
      </div>
      {filter !== null && (
        <button type="button" className="icon-button" onClick={() => onSelect(null)}>
          Clear
        </button>
      )}
    </div>
  );
}

function EmptyStates({
  totalRows,
  visibleRows,
}: {
  totalRows: number;
  visibleRows: number;
}): React.JSX.Element | null {
  if (totalRows === 0) {
    // Post-AD-22 every assigned Vendor gets a row (an un-evidenced one reads "No evidence
    // on file"), so an empty table means nothing is IN SCOPE — not that nothing was
    // assessed. The auditor PDF's twin of this string says "(no subjects in scope)".
    return <p className="muted" style={{ margin: 0 }}>No vendors are in scope for this framework yet.</p>;
  }
  if (visibleRows === 0) {
    return <p className="muted" style={{ margin: 0 }}>No rows match this filter.</p>;
  }
  return null;
}

function DeviceBody({
  rows,
  filter,
  setFilter,
}: {
  rows: FrameworkDeviceVendorRow[];
  filter: FilterKey | null;
  setFilter: (f: FilterKey | null) => void;
}): React.JSX.Element {
  const counts = useMemo<Record<FilterKey, number>>(() => {
    const c: Record<FilterKey, number> = { certified: 0, expiring: 0, atRisk: 0, pending: 0 };
    for (const r of rows) {
      const f = deviceFlags(r);
      for (const k of FILTER_ORDER) if (f[k]) c[k] += 1;
    }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const filtered = filter === null ? rows : rows.filter((r) => deviceFlags(r)[filter]);
    return [...filtered].sort(
      (a, b) => BAND_ORDER[a.band] - BAND_ORDER[b.band] || a.vendorName.localeCompare(b.vendorName),
    );
  }, [rows, filter]);

  return (
    <section className="card stack" aria-labelledby="fw-rows-heading">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 id="fw-rows-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Vendors</h2>
        <FilterChips counts={counts} filter={filter} onSelect={setFilter} />
      </div>

      {(rows.length === 0 || visible.length === 0) && (
        <EmptyStates totalRows={rows.length} visibleRows={visible.length} />
      )}

      {visible.length > 0 && (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Vendor</th>
              {/* The coverage column is pinned wide enough that every segment bar down the
                  column is measured against the SAME track — a proportional bar only reads
                  as a comparison if the thing it is a proportion of is a constant width. */}
              <th style={{ minWidth: 160 }}>Coverage</th>
              <th data-align="right">Certified</th>
              <th data-align="right">Expiring</th>
              <th data-align="right">Expired</th>
              <th data-align="right">Pending</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.vendorId}>
                <td>{r.vendorName}</td>
                <td>
                  <SegmentBar
                    segments={[
                      { tone: 'ok', label: 'Certified', count: r.certified },
                      { tone: 'warn', label: 'Expiring', count: r.expiring },
                      { tone: 'bad', label: 'Expired', count: r.expired },
                      { tone: 'info', label: 'Pending', count: r.pending },
                    ]}
                  />
                  <span className="muted" style={{ fontSize: 12 }} data-numeric>
                    {r.certified + r.expiring + r.expired + r.pending} of {r.deviceTotal} devices
                  </span>
                </td>
                <td data-align="right" data-numeric>{r.certified}</td>
                <td data-align="right" data-numeric>{r.expiring}</td>
                <td data-align="right" data-numeric>{r.expired}</td>
                <td data-align="right" data-numeric>{r.pending}</td>
                <td><BandPill band={r.band} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

function CompanyBody({
  rows,
  filter,
  setFilter,
}: {
  rows: FrameworkCompanyVendorRow[];
  filter: FilterKey | null;
  setFilter: (f: FilterKey | null) => void;
}): React.JSX.Element {
  const counts = useMemo<Record<FilterKey, number>>(() => {
    const c: Record<FilterKey, number> = { certified: 0, expiring: 0, atRisk: 0, pending: 0 };
    for (const r of rows) {
      const f = companyFlags(r);
      for (const k of FILTER_ORDER) if (f[k]) c[k] += 1;
    }
    return c;
  }, [rows]);

  const visible = useMemo(() => {
    const filtered = filter === null ? rows : rows.filter((r) => companyFlags(r)[filter]);
    return [...filtered].sort(
      (a, b) =>
        BAND_ORDER[a.band] - BAND_ORDER[b.band] ||
        expiryMillis(a.expiresAt) - expiryMillis(b.expiresAt) ||
        a.vendorName.localeCompare(b.vendorName),
    );
  }, [rows, filter]);

  return (
    <section className="card stack" aria-labelledby="fw-rows-heading">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 id="fw-rows-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Vendors</h2>
        <FilterChips counts={counts} filter={filter} onSelect={setFilter} />
      </div>

      {(rows.length === 0 || visible.length === 0) && (
        <EmptyStates totalRows={rows.length} visibleRows={visible.length} />
      )}

      {visible.length > 0 && (
        <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th>Status</th>
              <th>Expires</th>
              <th>Approving authority</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.vendorId}>
                <td>{r.vendorName}</td>
                <td><BandPill band={r.band} label={r.statusLabel} /></td>
                <td data-numeric>{shortDate(r.expiresAt)}</td>
                <td>{r.approvingAuthority ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}
